/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputComponent} from '../input/input.component';
import {UntypedFormBuilder, ValidationErrors, ValidatorFn} from '@angular/forms';
import {NgbDate, NgbInputDatepicker} from '@ng-bootstrap/ng-bootstrap';
import {DatepickerHelperService} from '../../utils/datepicker-helper/datepicker-helper.service';
import {WarningsEnum} from '../../warnings/warnings.enum';

@Component({
    selector: 'app-input-datepicker',
    templateUrl: './input-datepicker.component.html',
    styleUrls: [
        '../input/input.component.css',
        './input-datepicker.component.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InputDatepickerComponent extends InputComponent implements OnInit, OnDestroy {
    @Input() displayMonths = 1;
    @Input() minDate: NgbDate = null;
    @Input() maxDate: NgbDate = null;
    @Input() initialValue: NgbDate;
    @Input() dayTemplateData: NgbDate;
    @Input() placeholder = 'dd-mm-yyyy';
    @Input() expiringDate: boolean;
    @Input() timezone: string;
    @Input() animationStyle: { [key: string]: any };
    @Input() isDisabledCalendarButton: boolean;
    @Output() closedDatepicker = new EventEmitter();
    @ViewChild('datepicker') datepicker: NgbInputDatepicker;
    WarningsEnum = WarningsEnum;

    constructor(protected formBuilder: UntypedFormBuilder,
                public datepickerHelper: DatepickerHelperService) {
        super(formBuilder);
    }

    get datepickerCypressTag(): string {
        return `${this.baseCypressTag}-datepicker`;
    }

    get inputDateStyle(): { [key: string]: any } {
        let inputDateStyle = this.inputHeight;
        if (this.animationStyle != null) {
            inputDateStyle = {...this.animationStyle, ...this.inputHeight, ...this.isInvalidStyle};
        }
        return inputDateStyle;
    }

    get isInvalidStyle(): { [key: string]: any } {
        if (!this.isValid && this.animationStyle != null) {
            return {'color': '#d20000'};
        }
    }

    get todayDate(): NgbDate {
        return this.datepickerHelper.todayDate(this.timezone);
    }

    retrieveTemplateData = (): NgbDate | null => this.dayTemplateData ? this.dayTemplateData : null;

    datepickerValidator: ValidatorFn = (): ValidationErrors | null => {
        if (this.inputControl == null) {
            return null;
        }
        const value = this.inputControl.value;
        if (value == null || value === ''
                || value.constructor.name === NgbDate.name
                || (value.constructor.name === Object.name && Object.name !== 'String')) {
            return null;
        }
        return {'incorrect': true};
    };

    ngOnInit(): void {
        super.ngOnInit();
        this.setInputValidator(this.datepickerValidator);
    }

    closedDatepickerHandler(): void {
        if (!this.datepickerHelper.isSameDate(this.inputControl?.value, this.initialValue)) {
            this.closedDatepicker.emit(this.inputControl.value);
        }
    }

    selectDataHandler(date: NgbDate): void {
        if (date != null) {
            this.setValue(date);
        }
    }

    clickInput(event: any): void {
        if (this.minDate != null) {
            this.datepicker.startDate = {
                year: this.minDate.year,
                month: this.minDate.month
            };
        }
        this.inputClick.emit(event);
        this.datepicker.open();
    }
}
